/* eslint-disable @typescript-eslint/no-explicit-any */
import { Chip, Typography, useTheme } from '@material-ui/core'
import _ from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import Select, { ActionMeta, MultiValueProps, ValueType } from 'react-select'
import { GroupHeadingProps } from 'react-select/src/components/Group'
import { KeywordDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { useKeywordsTranslation } from './useKeywordsTranslation'

export type TokenSelectorProps = {
  selectedTokens: KeywordDto[]
  selectableTokens: KeywordDto[]
  onTokensChanges: (tokens: KeywordDto[]) => void
  width: string
}

const GroupHeading = (props: GroupHeadingProps<KeywordDto, true>) => {
  // console.log('groupHeading', props)
  const { t } = useTranslation()

  return (
    <div>
      <Typography variant="body2" color="textSecondary">
        {t(
          `project:material-category-${props['data'].category}`,
          props['data'].category
        )}
      </Typography>
    </div>
  )
}

const MultiValue = (props: MultiValueProps<KeywordDto>) => {
  const { translateKeyword } = useKeywordsTranslation()
  return (
    <div {...props.innerProps}>
      <Chip
        size="small"
        variant="outlined"
        key={props.data.originalKeyword}
        label={translateKeyword(props.data)}
        onDelete={() => props.removeProps.onClick({ values: props.data })}
        style={{ margin: '4px 8px 4px 0' }}
      />
    </div>
  )
}

const getSelectStyles = (theme) => ({
  control: (base: any) => ({
    ...base,
    minWidth: '100%',
    maxWidth: '100%',
    marginTop: '5px',
    marginBottom: '2px',
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    backgroundColor: theme.palette.background.paper,
  }),
  menu: () => ({ boxShadow: '0 1px 0 rgba(0, 0, 0, 0.1)' }),
  menuList: (base: any) => ({
    ...base,
    height: '100%',
    overflowY: 'auto',
    maxHeight: 'calc(60vh - 58px)',
  }),
  option: (base: any, { isSelected, isFocused }) => ({
    ...base,
    paddingLeft: '25px',
    paddingTop: '5px',
    paddingBottom: '5px',
    backgroundColor:
      isFocused || isSelected ? theme.palette.action.selected : '',
    '&:active': {
      backgroundColor: theme.palette.action.selected,
    },
    '&:hover': {
      cursor: 'pointer',
    },
  }),
  dropdownIndicator: (base: any) => ({ ...base, display: 'none' }),
  input: (base) => ({
    ...base,
    color: theme.palette.text.primary,
  }),
  // multiValue: (base: any) => ({
  //   ...base,
  //   backgroundColor: theme.palette.background.paper,
  //   color: theme.palette.getContrastText(theme.palette.background.paper),
  //   border: `2px solid ${theme.palette.divider}`,
  //   borderTopLeftRadius: '12px',
  //   borderBottomLeftRadius: '12px',
  //   borderTopRightRadius: '12px',
  //   borderBottomRightRadius: '12px',
  // }),
  // multiValueLabel: (base) => ({
  //   ...base,
  //   ...theme.typography.body2,
  //   color: theme.palette.text.primary,
  // }),
  // multiValueRemove: (base) => ({
  //   ...base,
  //   color: theme.palette.secondary.main,
  //   cursor: 'pointer',
  //   '&:hover': {
  //     backgroundColor: 'inherit',
  //   },
  // }),
})

export const TokenSelector = (props: TokenSelectorProps) => {
  const theme = useTheme()
  const selectStyles = useMemo(() => getSelectStyles(theme), [theme])
  const { i18n } = useTranslation()
  const { translateKeyword } = useKeywordsTranslation()

  const onTokensSelected = (
    value: ValueType<KeywordDto, true>,
    action: ActionMeta<KeywordDto>
  ) => {
    if (action.removedValue) {
      props.onTokensChanges(
        props.selectedTokens.filter(
          (token) =>
            token.originalKeyword !== action.removedValue.originalKeyword
        )
      )

      return
    }

    props.onTokensChanges((value || []).map((x) => x))
  }

  const isLoadingMaterials = useAppSelector(
    (state: RootAppState) =>
      state.app.currentNetworkAction === 'loading-materials' &&
      state.app.isLoading === true
  )

  return (
    <div style={{ width: props.width }}>
      <Select
        autoFocus={true}
        openMenuOnFocus={true}
        menuIsOpen={true}
        defaultMenuIsOpen={true}
        isMulti={true}
        isLoading={isLoadingMaterials}
        tabSelectsValue={false}
        closeMenuOnSelect={false}
        onChange={onTokensSelected}
        options={
          (isLoadingMaterials && []) ||
          Object.keys(_.groupBy(props.selectableTokens, (x) => x.category)).map(
            (key) => {
              return {
                category: key,
                options: props.selectableTokens
                  .filter((x) => x.category === key)
                  .sort((a, b) =>
                    a.originalKeyword.localeCompare(
                      b.originalKeyword,
                      i18n.language,
                      { numeric: true }
                    )
                  ),
              }
            }
          )
        }
        value={props.selectedTokens}
        styles={selectStyles}
        getOptionLabel={(option) => translateKeyword(option)}
        getOptionValue={(option) => option.originalKeyword}
        components={{
          GroupHeading,
          // Option: OptionComponent,
          MultiValue: MultiValue,
        }}
        //formatGroupLabel={formatGroupLabel}
        placeholder="Select tokens"
        classNamePrefix="materialSelector"
        menuShouldScrollIntoView={true}
        hideSelectedOptions={true}
      />
    </div>
  )
}
